import { useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { CheckCircle2, ChevronDown, ChevronRight, Loader2, RotateCcw, ShieldCheck } from 'lucide-react';
import { rbacOperations, type RbacOperation } from '@/api/client';
import { humanRole } from '@/lib/humanise';
import { RBAC_ROLE_GROUPS } from '@/lib/rbacModel';
import { useAuth } from '@/contexts/AuthContext';
import { errorText } from '@/lib/permissionError';

/**
 * The ENFORCED half of /admin/rbac.
 *
 * Every row here is an operation the backend guards with `requireOperation`,
 * so "Save and apply" genuinely changes who the API lets through — unlike the
 * page-level grid on the other tabs, which is still advisory.
 *
 * Only operations that have been migrated to the registry appear. That is
 * deliberate: showing an un-migrated operation here would imply the save takes
 * effect when it would not, which is the exact failure mode the advisory
 * banner on the other tabs exists to avoid.
 */

function sameRoles(a: readonly string[], b: readonly string[]): boolean {
  if (a.length !== b.length) return false;
  const set = new Set(a);
  return b.every((r) => set.has(r));
}

export function RbacOperationsPanel() {
  const qc = useQueryClient();
  const { user } = useAuth();
  const [expanded, setExpanded] = useState<string | null>(null);
  /** operation_key -> roles, for rows the user has touched this session. */
  const [draft, setDraft] = useState<Record<string, string[]>>({});

  const q = useQuery({
    queryKey: ['rbac-operations'],
    // Wrapped, not passed directly: list() now takes an optional siteId, and
    // react-query would hand it its own context object as that argument.
    queryFn: () => rbacOperations.list(),
    staleTime: 15_000,
  });

  const ops = useMemo(() => q.data?.operations ?? [], [q.data]);

  const siteId = q.data?.site_id;
  const siteLabel = siteId
    ? (user?.siteNames?.[siteId] ?? `site ${siteId}`)
    : 'this site';

  const pending = useMemo(
    () =>
      ops
        .filter((o) => draft[o.operation_key] && !sameRoles(draft[o.operation_key]!, o.effective_roles))
        .map((o) => ({ operation_key: o.operation_key, roles: draft[o.operation_key]! })),
    [ops, draft],
  );

  const saveM = useMutation({
    mutationFn: () => rbacOperations.save(pending),
    onSuccess: (res) => {
      const changed = res.applied.filter((a) => a.action !== 'unchanged').length;
      toast.success(
        changed === 0
          ? 'No changes to apply.'
          : `Applied to ${changed} operation${changed === 1 ? '' : 's'} — in force now.`,
      );
      setDraft({});
      void qc.invalidateQueries({ queryKey: ['rbac-operations'] });
    },
    onError: (e: unknown) =>
      toast.error(errorText(e, 'Could not apply the changes.')),
  });

  const rolesFor = (o: RbacOperation): string[] => draft[o.operation_key] ?? o.effective_roles;

  const toggleRole = (o: RbacOperation, role: string) => {
    const current = rolesFor(o);
    const next = current.includes(role) ? current.filter((r) => r !== role) : [...current, role];
    setDraft((d) => ({ ...d, [o.operation_key]: next }));
  };

  const revert = (o: RbacOperation) => {
    setDraft((d) => ({ ...d, [o.operation_key]: [...o.default_roles] }));
  };

  return (
    <div className="space-y-4">
      <div className="flex items-start gap-3 rounded border border-emerald-300 bg-emerald-50 px-4 py-3">
        <ShieldCheck className="h-4 w-4 text-emerald-700 mt-0.5 shrink-0" />
        <div className="text-sm text-emerald-900">
          <p className="font-semibold">
            These operations are enforced — for {siteLabel}.
          </p>
          <p className="mt-0.5 text-emerald-800">
            Saving changes what the API allows, immediately, and applies to this site only. Granting
            any verb also grants Read on that module, so a save can touch more operations than you
            edited. Operations not yet migrated to the registry do not appear — they are still
            governed by the code and shown on the other tabs as advisory only.
          </p>
        </div>
      </div>

      <div className="flex items-center justify-between gap-3 flex-wrap">
        <p className="text-xs text-ink-muted">
          {ops.length} enforced operation{ops.length === 1 ? '' : 's'}
          {pending.length > 0 && (
            <span className="ml-2 font-semibold text-violet-700">
              · {pending.length} unsaved change{pending.length === 1 ? '' : 's'}
            </span>
          )}
        </p>
        <div className="flex items-center gap-2">
          <button
            type="button"
            className="btn-secondary"
            disabled={pending.length === 0 || saveM.isPending}
            onClick={() => setDraft({})}
          >
            <RotateCcw className="h-3.5 w-3.5" />
            Discard changes
          </button>
          <button
            type="button"
            className="btn-primary"
            disabled={pending.length === 0 || saveM.isPending}
            onClick={() => saveM.mutate()}
          >
            {saveM.isPending ? (
              <Loader2 className="h-3.5 w-3.5 animate-spin" />
            ) : (
              <CheckCircle2 className="h-3.5 w-3.5" />
            )}
            Save and apply
          </button>
        </div>
      </div>

      <div className="rounded border border-surface-border bg-white overflow-hidden">
        {q.isLoading ? (
          <div className="p-10 text-center">
            <Loader2 className="h-4 w-4 animate-spin mx-auto text-ink-muted" />
          </div>
        ) : ops.length === 0 ? (
          <p className="p-10 text-center text-sm text-ink-muted">
            No operations are registered yet.
          </p>
        ) : (
          <ul className="divide-y divide-surface-border/60">
            {ops.map((o) => {
              const roles = rolesFor(o);
              const dirty = !sameRoles(roles, o.effective_roles);
              const isOpen = expanded === o.operation_key;
              const atDefault = sameRoles(roles, o.default_roles);
              return (
                <li key={o.operation_key}>
                  <button
                    type="button"
                    className="w-full text-left px-4 py-3 flex items-start gap-3 hover:bg-surface-muted/40"
                    onClick={() => setExpanded(isOpen ? null : o.operation_key)}
                  >
                    {isOpen ? (
                      <ChevronDown className="h-4 w-4 text-ink-muted mt-0.5 shrink-0" />
                    ) : (
                      <ChevronRight className="h-4 w-4 text-ink-muted mt-0.5 shrink-0" />
                    )}
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center gap-2 flex-wrap">
                        <code className="text-sm font-semibold text-ink">{o.operation_key}</code>
                        {dirty && (
                          <span className="text-[10px] font-semibold px-1.5 py-0.5 rounded-full bg-violet-100 text-violet-700">
                            unsaved
                          </span>
                        )}
                        {!dirty && o.overridden && (
                          <span className="text-[10px] font-semibold px-1.5 py-0.5 rounded-full bg-amber-100 text-amber-800">
                            overridden
                          </span>
                        )}
                      </div>
                      <div className="mt-1 flex flex-wrap gap-1">
                        {roles.length === 0 ? (
                          <span className="text-[11px] text-danger-600 font-semibold">
                            no roles — the server will refuse this
                          </span>
                        ) : (
                          roles.map((r) => (
                            <span
                              key={r}
                              className="text-[11px] px-1.5 py-0.5 rounded border border-surface-border bg-surface-muted/60 text-ink-muted"
                            >
                              {humanRole(r)}
                            </span>
                          ))
                        )}
                      </div>
                    </div>
                  </button>

                  {isOpen && (
                    <div className="px-4 pb-4 pt-1 bg-surface-muted/20 border-t border-surface-border/60">
                      <div className="flex items-center justify-between mb-2">
                        <p className="text-[11px] uppercase tracking-wider text-ink-muted font-semibold">
                          Who may perform this
                        </p>
                        <button
                          type="button"
                          className="text-[11px] text-ink-muted hover:text-ink underline disabled:opacity-40 disabled:no-underline"
                          disabled={atDefault}
                          onClick={() => revert(o)}
                          title="Restore the roles this operation ships with"
                        >
                          Revert to default
                        </button>
                      </div>
                      {RBAC_ROLE_GROUPS.map((group) => (
                        <div key={group.label} className="mb-2 last:mb-0">
                          <p className="text-[10px] uppercase tracking-wider text-ink-muted mb-1">
                            {group.label}
                          </p>
                          <div className="flex flex-wrap gap-x-4 gap-y-1">
                            {group.roles.map((role) => (
                              <label
                                key={role}
                                className="flex items-center gap-1.5 text-xs text-ink cursor-pointer"
                              >
                                <input
                                  type="checkbox"
                                  className="h-3.5 w-3.5 rounded border-surface-border accent-navy-600"
                                  checked={roles.includes(role)}
                                  onChange={() => toggleRole(o, role)}
                                />
                                {humanRole(role)}
                              </label>
                            ))}
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </li>
              );
            })}
          </ul>
        )}
      </div>
    </div>
  );
}
